import { ScreenVariations, useScreenContext } from "../../library/context/screenview"
import chefstepsLogo from '../../assets/chefsteps-logo.png';
import Cook from '../../assets/cook.svg';
import User from '../../assets/user.svg';
import SquareUp from '../../assets/arrow-up-circle-fill.svg';
import StopIcon from '../../assets/circle-stop-solid.svg'
import ArrowDownCircle from '../../assets/arrow-down-circle-fill.svg';
import Form from 'react-bootstrap/Form';
import '../scss/chat.scss';
import { initialMessage } from "./helper";
import { useSession } from "../../hooks/useSession";
import { appConfig } from "../../library/utils/appConfig";
import { useAssistantChat, Message } from "../../hooks/useAssistantChat";
import { FormEvent, useEffect, useRef, useState } from "react";
import Markdown from 'markdown-to-jsx'
import { Loader } from "../../components/Loader";
import RecipeCardComp from "../../components/RecipeCard";
import { CarouselArrow } from "@breville/xps-react/dist";

export type ChatProps = {
    screenName: ScreenVariations;
}

export type AssistantProps = {
    role: 'system' | 'user' | 'assistant' | 'function' | 'data' | 'tool';
    content: any;
    data?: any;
    triggerMessageSubmit?: any;
}

// Single source of truth — same config the session hook reads (config.{env}.json).
const PROXY_URL = appConfig.proxyUrl;

function Card(props: any) {
    const {title, image_id, slug} = props
    const parsedImageId = JSON.parse(image_id||'{}');
    const author = props.byline || 'ChefSteps';
    const thumbnailImage = parsedImageId?.url || 'https://staging-app.breville.com/thumbnail/recipeAuthor/1600136397_chefsteps_300_red_376x500.png';
    const href = slug ? `https://www.chefsteps.com/activities/${slug}` : '#';
    const handleClickRecipe = (href: string) => {
        window.open(href, '_blank', 'noopener,noreferrer');
      };
    return (
    <div className='chat-display-card' onClick ={()=>handleClickRecipe(href)}>
        <RecipeCardComp title={title} src={thumbnailImage} author={author}/>
    </div>)

}

function ChatMessageCard(props: AssistantProps) {
    const {role, content, data, triggerMessageSubmit} = props;

    // if(role === 'data') {
    //     return(
    //     <div className='chat-display-cardlist'>
    //         {data?.map((each: any, index: any)=><Card key={index} {...each} />)}
    //     </div>)
    // }

    const isData = role === 'data';
    return(
        <div className="chat-message-card">
            <div className="chat-role-logo me-4">{role === 'assistant' || role === 'data' ? <Cook /> : <User />}</div>
            {isData ? 
                <div className='chat-display-cardlist'>
                {data?.filter((each: any, index: any)=>each?.image_id !== '').map((each: any, index: any)=><Card key={index} triggerMessageSubmit={triggerMessageSubmit} {...each} />)}
                </div> : 
                <div className="chat-message-content">
                    <Markdown>
                        {content}
                    </Markdown>
                </div>
            }
        </div>
    )
}

export function Chat(props: ChatProps){

    // @ts-ignore
    const firstMessage = initialMessage[props.screenName] || {};
    const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);
    const [contentBehindFold, setContentBehindFold] = useState(false);

    const buttonRef = useRef(null);
    const formRef = useRef<HTMLFormElement | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const { tokens, refreshSess } = useSession();
    const { messages, input, handleInputChange, submitMessage, setInput, setMessages, stop, status } = useAssistantChat({
        proxyUrl: PROXY_URL,
        tokens,
        onSessRefresh: refreshSess,
        screenName: props.screenName,
    });

    const isLoading = status === 'in_progress';

    const { setCurrentScreen, currentScreen } = useScreenContext();
    
    const message: Message[] = firstMessage.content ? [firstMessage,...messages] : messages;
    const handleMessageSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        submitMessage(e);
        scrollToBottom();
        if(!currentScreen) setCurrentScreen('defaultChoice');
        if(textareaRef.current) textareaRef.current.style.height = 'auto';
    };
    function handleBackToHome() {
        stop()
        setMessages([])
        setCurrentScreen(null)
    }

    
    function triggerMessageSubmit(data: any) {
       
        setInput(data)

        setTimeout(()=>{
            // @ts-ignore
            if(buttonRef.current) buttonRef.current.click();
        }, 100)
    }

    useEffect(()=>{
        if (isAutoScrollEnabled && containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
    },[messages, isAutoScrollEnabled])

    useEffect(() => {
        const container = containerRef.current;
        if (container) {
          container.addEventListener('scroll', handleScroll);
        }
    
        return () => {
          if (container) {
            container.removeEventListener('scroll', handleScroll);
          }
        };
      }, []);

    function handleScroll() {
        if (containerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
            const calc = Math.round(scrollHeight - scrollTop) - clientHeight;
            const isAtBottom = calc < 5;
            const hasContentBehindFold = calc > 5;

            setContentBehindFold(hasContentBehindFold);

            if(!isAtBottom) {
                setIsAutoScrollEnabled(false);
            }
            else{
                setIsAutoScrollEnabled(true);
            
            }
          }
    }

    // Auto resize the textarea
    const handleInputAutoResize = () => {
        const textarea = textareaRef.current;
        if (textarea) {
            // Reset height to auto to shrink the textarea before expanding
            textarea.style.height = 'auto';

            // Set the height to the scrollHeight (height of the content)
            const newHeight = Math.min(textarea.scrollHeight, 80); // max 80px (3 lines)
            textarea.style.height = `${newHeight}px`;
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        // If Enter key is pressed, submit the message
        if (e.key === 'Enter') {
            if (!e.shiftKey) {
                e.preventDefault();
                formRef.current?.requestSubmit();
            }
            if(textareaRef.current) textareaRef.current.style.height = 'auto';
        }
    };

    function scrollToBottom() {
        if (containerRef.current) {
          containerRef.current.scrollTo({
            top: containerRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
    }

    
    return (
        <div style={{height: '100%'}}>
            <div className="chat-wrapper">
                
                <div className="chat-header">
                    { message.length > 0 &&
                    <div className="chat-back-btn">
                        <CarouselArrow
                            onClick={handleBackToHome}
                            size="sm"
                            theme="inherit"
                            type="left"
                            variant="light"
                        /> </div>}
                    <a className="chef-steps-logo" href={appConfig.shellAppUrl}><img width={148} height={38} src = {chefstepsLogo} alt="ChefSteps"/></a>
                </div>
                <div ref={containerRef} className="chat-body" id="brevilleplus-chat-screen" onScrollCapture={handleScroll}>
                    {message.map(each=><ChatMessageCard key={each.id} {...each} triggerMessageSubmit={triggerMessageSubmit}/>)}
                    {isLoading && <Loader />}
                </div>
                <div className="chat-input-box-wrapper">
                    {contentBehindFold && (
                        <button
                            className="btn-behind-thefold position-absolute"
                            onClick={scrollToBottom}
                        >
                            <ArrowDownCircle />
                        </button>
                    )}
                    <Form onSubmit={handleMessageSubmit} ref={formRef} className="chat-input">
                        <Form.Control
                            as="textarea"
                            className="chat-input-box"
                            value={input}
                            placeholder="Ask me anything"
                            onChange={handleInputChange}
                            onInput={handleInputAutoResize} // Automatically resize the textarea
                            onKeyDown={handleKeyDown}
                            rows={1}
                            ref={textareaRef}
                            style={{ height: 'auto' }}
                        />
                        {isLoading ? (
                            <div>
                                <button
                                    type="button"
                                    className="submit-message-button"
                                    onClick={() => stop()}
                                >
                                    <StopIcon />
                                </button>
                            </div>
                        ) : (
                            <button
                                className={`submit-message-button ${input.length === 0 ? 'disabled' : ''}`}
                                type="submit"
                                disabled={input.length === 0}
                                ref={buttonRef}
                            >
                                <SquareUp />
                            </button>
                        )}
                        <input type="submit" ref={buttonRef} style={{ display: 'none' }} />
                    </Form>
                </div>
            </div>
        </div>
    )

}